// Issue an equity award certificate — per-company branding + per-award
// fields, rendered server-side (POST /legal/certificate), filed into the
// deal as a document, and downloaded. Companion to the signatures flow:
// certificates are the celebratory artifact of a grant that already lives
// in an executed agreement here.
import React, { useState } from 'react';
import Modal from './Modal';
import { useToast } from './Toast';
import { useOrg } from '../data/orgs';
import { issueCertificate } from '../data/legalApi';

const today = () => new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const Field = ({ label, children, grow }) => (
  <div className="cs-field" style={{ marginBottom: 0, ...(grow ? { flex: 1 } : {}) }}>
    <label>{label}</label>
    {children}
  </div>
);

export default function CertificateModal({ scope, deal, docs, onClose }) {
  const toast = useToast();
  const { org } = useOrg();
  const agreements = (docs || []).filter((d) => d.status === 'ready' && d.currentVersionId);

  const [companyName, setCompanyName] = useState('');
  const [subline, setSubline] = useState('');
  const [brandLine, setBrandLine] = useState(org?.name ? `an ${org.name} brand` : '');
  const [logoDataUrl, setLogoDataUrl] = useState('');
  const [title, setTitle] = useState('Equity Award Certificate');
  const [certificateNo, setCertificateNo] = useState('1');
  const [holderName, setHolderName] = useState('');
  const [classLabel, setClassLabel] = useState('Units');
  const [denomination, setDenomination] = useState('');
  const [bodyText, setBodyText] = useState('');
  const [milestoneText, setMilestoneText] = useState('');
  const [issuedDate, setIssuedDate] = useState(today());
  const [issuedPlace, setIssuedPlace] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerTitle, setSignerTitle] = useState('Manager');
  const [signerOrg, setSignerOrg] = useState('');
  const [agreementDocId, setAgreementDocId] = useState('');
  const [legendText, setLegendText] = useState('');
  const [busy, setBusy] = useState(false);

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!/image\/(png|jpe?g)/.test(f.type)) { toast('Logo must be a PNG or JPEG', 'danger'); return; }
    if (f.size > 500 * 1024) { toast('Logo too large (max 500 KB)', 'danger'); return; }
    const reader = new FileReader();
    reader.onload = () => setLogoDataUrl(String(reader.result));
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await issueCertificate({
        orgId: scope.org,
        dealId: scope.deal,
        company: { name: companyName.trim(), subline: subline.trim(), brandLine: brandLine.trim(), logoDataUrl: logoDataUrl || undefined },
        award: {
          title: title.trim(),
          certificateNo: certificateNo.trim(),
          holderName: holderName.trim(),
          classLabel: classLabel.trim(),
          denomination: denomination.trim(),
          bodyText: bodyText.trim(),
          milestoneText: milestoneText.trim(),
          issuedDate: issuedDate.trim(),
          issuedPlace: issuedPlace.trim(),
        },
        signer: { name: signerName.trim(), title: signerTitle.trim(), org: signerOrg.trim() },
        legendText: legendText.trim() || undefined,
        agreementDocId: agreementDocId || undefined,
      });
      toast('Certificate issued, filed, and downloaded', 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Issue equity certificate" onClose={onClose} maxWidth={640}>
      <form onSubmit={submit} className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
        <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          <strong className="u-small">Issuer</strong>
          <div className="u-cluster" style={{ '--gap': 'var(--sp-3)' }}>
            <Field label="Company name" grow>
              <input className="cs-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="Azimuth GS Holdings, LLC" required />
            </Field>
            <Field label="Brand line" grow>
              <input className="cs-input" value={brandLine} onChange={(e) => setBrandLine(e.target.value)} placeholder="an HGM brand" />
            </Field>
          </div>
          <Field label="Subline (under the mark)">
            <input className="cs-input" value={subline} onChange={(e) => setSubline(e.target.value)} placeholder="A Nevada Limited Liability Company" />
          </Field>
          <Field label="Logo (optional PNG/JPEG — otherwise the engraved compass mark)">
            <input className="cs-input" type="file" accept="image/png,image/jpeg" onChange={onLogo} />
          </Field>
        </div>

        <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          <strong className="u-small">Award</strong>
          <div className="u-cluster" style={{ '--gap': 'var(--sp-3)' }}>
            <Field label="Holder name" grow>
              <input className="cs-input" value={holderName} onChange={(e) => setHolderName(e.target.value)} placeholder="Joshua Settle" required />
            </Field>
            <Field label="Certificate no.">
              <input className="cs-input" value={certificateNo} onChange={(e) => setCertificateNo(e.target.value)} style={{ width: 90 }} />
            </Field>
          </div>
          <div className="u-cluster" style={{ '--gap': 'var(--sp-3)' }}>
            <Field label="Class label" grow>
              <input className="cs-input" value={classLabel} onChange={(e) => setClassLabel(e.target.value)} placeholder="Class B Units" />
            </Field>
            <Field label="Denomination (unit/share count — not a naked %)" grow>
              <input className="cs-input" value={denomination} onChange={(e) => setDenomination(e.target.value)} placeholder="1,764.71" />
            </Field>
            <Field label="Certificate title" grow>
              <input className="cs-input" value={title} onChange={(e) => setTitle(e.target.value)} />
            </Field>
          </div>
          <Field label="Body — “This certifies that [holder] …” (denominate in units; state any % as a dated snapshot with the full denominator)">
            <textarea className="cs-input" rows={4} value={bodyText} onChange={(e) => setBodyText(e.target.value)}
              placeholder="is the registered holder of 1,764.71 Class B Units of …, fully vested and issued as of … — representing, as of the date of issuance, fifteen percent (15%) of the 11,764.71 Units then issued and outstanding — pursuant to the … Agreement dated …, and held subject to the Company’s Limited Liability Company Agreement."
              required />
          </Field>
          <Field label="Milestone note (optional italic line)">
            <textarea className="cs-input" rows={2} value={milestoneText} onChange={(e) => setMilestoneText(e.target.value)}
              placeholder="A further fifteen percent (15%) stands reserved for the Holder, vesting upon …" />
          </Field>
          <div className="u-cluster" style={{ '--gap': 'var(--sp-3)' }}>
            <Field label="Issued date" grow>
              <input className="cs-input" value={issuedDate} onChange={(e) => setIssuedDate(e.target.value)} />
            </Field>
            <Field label="Place (optional)" grow>
              <input className="cs-input" value={issuedPlace} onChange={(e) => setIssuedPlace(e.target.value)} placeholder="State of Nevada" />
            </Field>
          </div>
        </div>

        <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          <strong className="u-small">Signature & evidence</strong>
          <div className="u-cluster" style={{ '--gap': 'var(--sp-3)' }}>
            <Field label="Signer name" grow>
              <input className="cs-input" value={signerName} onChange={(e) => setSignerName(e.target.value)} required />
            </Field>
            <Field label="Signer title" grow>
              <input className="cs-input" value={signerTitle} onChange={(e) => setSignerTitle(e.target.value)} />
            </Field>
          </div>
          <Field label="Signer organization line (optional)">
            <input className="cs-input" value={signerOrg} onChange={(e) => setSignerOrg(e.target.value)} placeholder="HandsOn Global Management, LLC, Manager of …" />
          </Field>
          <Field label="Underlying agreement (adds the sha256 evidence footer when sealed)">
            <select className="cs-input" value={agreementDocId} onChange={(e) => setAgreementDocId(e.target.value)}>
              <option value="">None</option>
              {agreements.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </Field>
          <Field label="Legend fine print (optional — defaults to a securities/transfer-restriction legend)">
            <textarea className="cs-input" rows={2} value={legendText} onChange={(e) => setLegendText(e.target.value)}
              placeholder="The units represented hereby have not been registered under the Securities Act of 1933 …" />
          </Field>
        </div>

        <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy}>
            {busy ? 'Issuing…' : 'Issue & download'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
